import { styled } from '@linaria/react';
import { Stack } from '@mantine/core';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  getStraightPath,
  Position,
  type EdgeProps,
} from '@xyflow/react';
import { AgentEventTypes, type AgentEventLog } from '../../../../api/events-client/events-client.type';
import { baseWhite, inkLight, redBase } from '../../../../styles/design-tokens';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { formatAgenticProcessDate } from '../../agentic-process-helpers';
import { AGENT_SOURCES_LABELS, EVENT_TYPES_LABELS } from '../../agentic-process.type';

export const AgentEventEdge = ({ sourceX, sourceY, targetX, targetY, style = {}, data }: EdgeProps) => {
  const event: AgentEventLog = data?.event as AgentEventLog;
  const setSelectedEvent = data?.setSelectedEvent as (event: AgentEventLog) => void;
  const connectToSelf = data?.connectToSelf === true;
  const selectedEvent = data?.selectedEvent as AgentEventLog | null;

  const isSelected = !!(event && selectedEvent && selectedEvent.id === event.id);

  const eventType = event ? (EVENT_TYPES_LABELS[event.event_type] ?? event.event_type) : '';

  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (connectToSelf) {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition: Position.Right,
      targetPosition: Position.Right,
      borderRadius: 10,
      offset: 20,
    });
  } else {
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  }

  const handleClick = () => {
    if (setSelectedEvent) {
      setSelectedEvent(event);
    }
  };

  const getTooltipContent = () => {
    if (!event) return '';

    const lines = [
      `Event Type: ${eventType}`,
      `Agent Source: ${AGENT_SOURCES_LABELS[event.agent_source]}`,
      `Date: ${formatAgenticProcessDate(event.event_date)}`,
    ];

    if (event.llm_model) {
      lines.push(`LLM: ${event.llm_model}`);
    }

    return (
      <Stack gap={0}>
        {lines.map((line, index) => (
          <Text key={index}>{line}</Text>
        ))}
      </Stack>
    );
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={isSelected ? 'url(#agent-event-arrow-selected)' : 'url(#agent-event-arrow)'}
        style={{
          stroke: isSelected ? redBase : 'var(--base-black)',
          strokeWidth: 2,
          ...style,
        }}
      />
      <defs>
        <marker
          id="agent-event-arrow"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill="var(--base-black)" />
        </marker>
        <marker
          id="agent-event-arrow-selected"
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill={redBase} />
        </marker>
      </defs>
      <EdgeLabelRenderer>
        {event && (
          <Tooltip label={getTooltipContent()} variant="black" multiline>
            <EdgeLabelContainer
              onClick={handleClick}
              style={{
                transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              }}
              isSelected={isSelected || false}
            >
              {event.event_type === AgentEventTypes.TOOL_CALL ||
              (event.event_type === AgentEventTypes.TOOL_RESULT && event.metadata?.tool_name) ? (
                <Text size="small">{event.metadata?.tool_name}</Text>
              ) : (
                <Text size="small">{eventType}</Text>
              )}
            </EdgeLabelContainer>
          </Tooltip>
        )}
      </EdgeLabelRenderer>
    </>
  );
};

const EdgeLabelContainer = styled.div<{ isSelected?: boolean }>`
  position: absolute;
  pointer-events: all;
  z-index: 10;
  background-color: ${baseWhite};
  border-radius: 4px;
  padding: 2px 6px;
  border: 1px solid ${({ isSelected }) => (isSelected ? redBase : inkLight)};
  cursor: pointer;
`;
